import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowRight, CheckCircle, BarChart3, FileText, Lock, Brain, Sparkles, Eye, Zap, Target, Cpu, ClipboardCheck, Building2, BadgeCheck } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();

  const strengths = [
    {
      icon: Eye,
      title: 'Pattern Recognition',
      description: 'Spot anomalies and security gaps that others miss. Our team sees the signals in the noise.'
    },
    {
      icon: Target,
      title: 'Precision Focus',
      description: 'Deep concentration on complex security assessments. Every detail matters, and we catch them all.'
    },
    {
      icon: Brain,
      title: 'Logical Thinking',
      description: 'Systematic analysis of security frameworks. When logic is your native language, threats become puzzles to solve.'
    },
    {
      icon: Zap,
      title: 'Innovative Solutions',
      description: 'Fresh perspectives on cybersecurity challenges. Different minds find different solutions.'
    }
  ];

  const features = [
    {
      icon: Shield,
      title: 'CMMC Assessment',
      description: 'Evaluate your organization against Cybersecurity Maturity Model Certification requirements.'
    },
    {
      icon: FileText,
      title: 'HIPAA Compliance',
      description: 'Assess compliance with HIPAA Security Rule requirements for healthcare organizations.'
    },
    {
      icon: BarChart3,
      title: 'Detailed Reports',
      description: 'Generate comprehensive PDF reports with scores, findings, and remediation steps.'
    },
    {
      icon: Lock,
      title: 'Secure & Private',
      description: 'Your assessment data is stored securely and only accessible to authorized users.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Glowing orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-violet-600/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-6 py-24 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-500/20 to-purple-500/20 rounded-full text-violet-300 text-sm mb-8 border border-violet-500/30 backdrop-blur-sm">
              <Brain className="w-4 h-4" />
              When Logic Is Your Native Language
            </div>
            
            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400">Masks Down.</span>
              <br />
              <span className="text-white">Shields Up.</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-violet-200 mb-6 max-w-3xl mx-auto font-light">
              Neurodivergent minds see what others miss.
            </p>
            <p className="text-lg text-violet-300/80 mb-10 max-w-2xl mx-auto">
              Our team's unique cognitive strengths—pattern recognition, deep focus, and systematic thinking—make us natural cyber defenders. 
              We don't just find vulnerabilities. We anticipate them.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                data-testid="hero-get-started-btn"
                onClick={() => navigate('/register')}
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-violet-500/25 transform hover:scale-105"
              >
                <Shield className="w-5 h-5" />
                Launch Assessment
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-4 border border-violet-400/50 text-violet-200 hover:bg-violet-500/10 hover:border-violet-400 font-semibold rounded-xl transition-all backdrop-blur-sm"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Neurodivergent Strengths Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Different Minds. <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">Superior Defense.</span>
          </h2>
          <p className="text-violet-300 max-w-2xl mx-auto text-lg">
            The same traits that make us think differently make us think <em>better</em> about security.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {strengths.map((strength, index) => (
            <div
              key={index}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 to-purple-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-slate-800/50 backdrop-blur-lg rounded-2xl p-8 border border-violet-500/20 hover:border-violet-500/50 transition-all h-full">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-violet-500/25">
                  <strength.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{strength.title}</h3>
                <p className="text-violet-200/80">{strength.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Assessment Types */}
      <section className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Assessment Frameworks</h2>
          <p className="text-violet-300 max-w-2xl mx-auto text-lg">
            Industry-standard frameworks, assessed with extraordinary attention to detail.
          </p>
        </div>
        
        {/* First row - 3 frameworks */}
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 rounded-2xl p-8 border border-emerald-500/30 hover:border-emerald-400/50 transition-all group">
            <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <FileText className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">HIPAA</h3>
            <p className="text-emerald-400 text-sm font-medium mb-4">158 Questions • Healthcare Security</p>
            <p className="text-emerald-200/80 mb-6">
              Assess compliance with the HIPAA Security Rule for healthcare organizations protecting ePHI.
            </p>
            <ul className="space-y-2 text-emerald-200/70 text-sm">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                Administrative Safeguards
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                Physical Safeguards
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                Technical Safeguards
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-2xl p-8 border border-blue-500/30 hover:border-blue-400/50 transition-all group">
            <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">CMMC</h3>
            <p className="text-blue-400 text-sm font-medium mb-4">125 Questions • Defense Contractors</p>
            <p className="text-blue-200/80 mb-6">
              Evaluate compliance with CMMC 2.0 framework for DoD contractors handling CUI.
            </p>
            <ul className="space-y-2 text-blue-200/70 text-sm">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-400" />
                Access Control
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-400" />
                Identification & Auth
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-400" />
                System Integrity
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 rounded-2xl p-8 border border-purple-500/30 hover:border-purple-400/50 transition-all group">
            <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <Cpu className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">NIST CSF</h3>
            <p className="text-purple-400 text-sm font-medium mb-4">106 Questions • Comprehensive Framework</p>
            <p className="text-purple-200/80 mb-6">
              Comprehensive framework covering Govern, Identify, Protect, Detect, Respond, and Recover.
            </p>
            <ul className="space-y-2 text-purple-200/70 text-sm">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400" />
                Risk Management
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400" />
                Asset Protection
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400" />
                Incident Response
              </li>
            </ul>
          </div>
        </div>

        {/* Second row - 2 frameworks centered */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 rounded-2xl p-8 border border-orange-500/30 hover:border-orange-400/50 transition-all group">
            <div className="w-14 h-14 bg-orange-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <BadgeCheck className="w-8 h-8 text-orange-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">SOC 2</h3>
            <p className="text-orange-400 text-sm font-medium mb-4">110 Questions • Trust Services Criteria</p>
            <p className="text-orange-200/80 mb-6">
              Evaluate controls for security, availability, processing integrity, confidentiality, and privacy.
            </p>
            <ul className="space-y-2 text-orange-200/70 text-sm">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-orange-400" />
                Security Controls
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-orange-400" />
                Availability & Integrity
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-orange-400" />
                Confidentiality & Privacy
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-2xl p-8 border border-amber-500/30 hover:border-amber-400/50 transition-all group">
            <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <Building2 className="w-8 h-8 text-amber-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">FINRA</h3>
            <p className="text-amber-400 text-sm font-medium mb-4">85 Questions • Financial Services</p>
            <p className="text-amber-200/80 mb-6">
              Assess cybersecurity compliance for broker-dealers and financial services organizations.
            </p>
            <ul className="space-y-2 text-amber-200/70 text-sm">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-amber-400" />
                Data Protection
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-amber-400" />
                Access Management
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-amber-400" />
                Incident Response
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Self-Assessment CTA Section */}
      <section className="container mx-auto px-6 py-16">
        <div className="bg-gradient-to-br from-cyan-500/10 via-violet-500/10 to-purple-500/10 rounded-3xl p-8 md:p-12 border border-cyan-500/20 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-violet-500/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 rounded-full text-cyan-300 text-sm mb-4 border border-cyan-500/30">
                <ClipboardCheck className="w-4 h-4" />
                Free Self-Assessment
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Take Our CMMC Readiness<br />Self-Assessment Now
              </h2>
              <p className="text-violet-200 mb-6 max-w-xl">
                Not sure where you stand? Our quick 17-question assessment will evaluate your organization's cybersecurity readiness and identify areas for improvement—no commitment required.
              </p>
              <ul className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-violet-300 mb-8">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-400" />
                  Takes 5 minutes
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-400" />
                  Instant results
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-400" />
                  Personalized recommendations
                </li>
              </ul>
              <button
                onClick={() => navigate('/self-assessment')}
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-500/25 transform hover:scale-105 mx-auto lg:mx-0"
              >
                <ClipboardCheck className="w-5 h-5" />
                Start Free Assessment
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-shrink-0">
              <div className="w-64 h-64 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-violet-500/20 rounded-2xl transform rotate-6" />
                <div className="absolute inset-0 bg-slate-800/90 backdrop-blur-lg rounded-2xl border border-cyan-500/30 p-6 flex flex-col justify-center items-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-white font-bold text-lg mb-1">CMMC Readiness</p>
                  <p className="text-cyan-400 text-sm">Self-Assessment</p>
                  <div className="mt-4 w-full bg-slate-700 rounded-full h-2">
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full w-3/4" />
                  </div>
                  <p className="text-slate-400 text-xs mt-2">17 questions • 5 min</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Built for Thoroughness</h2>
          <p className="text-violet-300 max-w-2xl mx-auto text-lg">
            Comprehensive tools designed with the same meticulous attention to detail we bring to everything.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-violet-500/20 hover:border-violet-500/50 transition-all group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:from-violet-500/30 group-hover:to-purple-500/30 transition-colors">
                <feature.icon className="w-6 h-6 text-violet-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-violet-200/80 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-violet-700 rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
          
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/90 text-sm mb-6 backdrop-blur-sm">
              <Sparkles className="w-4 h-4" />
              Join the defense
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Think Differently About Security?
            </h2>
            <p className="text-violet-100 mb-8 max-w-xl mx-auto text-lg">
              Start your assessment today. Let our unique perspective strengthen your defenses.
            </p>
            <button
              onClick={() => navigate('/register')}
              className="px-10 py-4 bg-white text-violet-600 font-semibold rounded-xl hover:bg-violet-50 transition-all shadow-lg transform hover:scale-105"
            >
              Create Free Account
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-6 py-12 border-t border-violet-500/20">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-white font-semibold">Aspiritech</span>
              <span className="text-violet-400 text-sm block">Cybersecurity Assessment Tool</span>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <a href="https://aspiritech.org" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-white transition-colors">
              About Aspiritech
            </a>
            <a href="https://aspiritech.org/services/cybersecurity/" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-white transition-colors">
              Cybersecurity Services
            </a>
            <a href="https://aspiritech.org/our-story/contact-us/" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-white transition-colors">
              Contact
            </a>
          </div>
          <p className="text-violet-400/60 text-sm">
            © {new Date().getFullYear()} Aspiritech • Masks Down, Shields Up
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
